import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getMonitor, getMonitorRuns, getMonitorAlerts, deleteMonitor } from '../services/api';
import StatusBadge from '../components/StatusBadge';
import ErrorModal from '../components/ErrorModal';

const MonitorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [monitor, setMonitor] = useState(null);
  const [runs, setRuns] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('runs');
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [monitorRes, runsRes, alertsRes] = await Promise.all([
        getMonitor(id),
        getMonitorRuns(id, 50),
        getMonitorAlerts(id),
      ]);
      setMonitor(monitorRes.data);
      setRuns(runsRes.data);
      setAlerts(alertsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading monitor:', error);
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleDelete = async () => {
    try {
      await deleteMonitor(id);
      navigate('/');
    } catch (error) {
      console.error('Error deleting monitor:', error);
      setError('Failed to delete monitor: ' + (error.response?.data?.error || error.message));
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600"></div>
        <p className="mt-4 text-gray-500">Loading monitor details...</p>
      </div>
    );
  }

  if (!monitor) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 border-red-200 dark:border-red-800 p-8 text-center">
        <div className="text-red-500 dark:text-red-400 text-6xl mb-4">⚠️</div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Monitor Not Found</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">The monitor you're looking for doesn't exist.</p>
        <Link to="/" className="text-blue-600 hover:text-blue-700 font-semibold">
          ← Back to Overview
        </Link>
      </div>
    );
  }

  const latestRun = runs[0];
  const uptime = runs.filter((r) => r.ok).length / runs.length * 100;
  const avgLatency = runs.length > 0 
    ? Math.round(runs.reduce((sum, r) => sum + (r.latencyMs || 0), 0) / runs.length)
    : 0;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-lg p-6 mb-6 text-white">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <Link to="/" className="text-blue-100 hover:text-white mr-4">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </Link>
              <h1 className="text-3xl font-bold">{monitor.name}</h1>
            </div>
            <p className="text-blue-100 font-mono text-sm mt-1 break-all">{monitor.url}</p>
          </div>
          <div className="flex space-x-2">
            <Link
              to={`/monitor/${id}/edit`}
              className="bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 px-5 py-2 rounded-lg font-semibold hover:bg-blue-50 dark:hover:bg-gray-600 shadow-md hover:shadow-lg transition-all flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </Link>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="bg-red-500 dark:bg-red-600 text-white px-5 py-2 rounded-lg font-semibold hover:bg-red-600 dark:hover:bg-red-700 shadow-md hover:shadow-lg transition-all flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Status</div>
            <div className={`w-3 h-3 rounded-full ${latestRun?.ok ? 'bg-green-500 animate-pulse' : 'bg-red-500 animate-pulse'}`}></div>
          </div>
          <StatusBadge status={latestRun?.ok} enabled={monitor.enabled} />
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 rounded-xl shadow-lg border border-green-200 dark:border-green-700 p-6">
          <div className="text-sm font-medium text-green-700 dark:text-green-400 mb-1">Uptime</div>
          <div className="text-3xl font-bold text-green-900 dark:text-green-300">{isNaN(uptime) ? 'N/A' : `${uptime.toFixed(1)}%`}</div>
          <div className="text-xs text-green-600 dark:text-green-400 mt-1">Last {runs.length} checks</div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-xl shadow-lg border border-blue-200 dark:border-blue-700 p-6">
          <div className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-1">Avg Latency</div>
          <div className="text-3xl font-bold text-blue-900 dark:text-blue-300">{avgLatency}ms</div>
          <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">Average response time</div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-xl shadow-lg border border-purple-200 dark:border-purple-700 p-6">
          <div className="text-sm font-medium text-purple-700 dark:text-purple-400 mb-1">Last Check</div>
          <div className="text-sm font-semibold text-purple-900 dark:text-purple-300">
            {latestRun ? new Date(latestRun.ts).toLocaleString() : 'Never'}
          </div>
            {latestRun && (
            <div className="text-xs text-purple-600 dark:text-purple-400 mt-1">
              {Math.round((Date.now() - new Date(latestRun.ts).getTime()) / 1000 / 60)} min ago
            </div>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('runs')}
              className={`px-6 py-4 text-sm font-semibold transition-all ${
                activeTab === 'runs'
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-800'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Check Runs
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === 'runs' ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}>
                  {runs.length}
                </span>
              </span>
            </button>
            <button
              onClick={() => setActiveTab('alerts')}
              className={`px-6 py-4 text-sm font-semibold transition-all ${
                activeTab === 'alerts'
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-800'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                Alerts
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === 'alerts' ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}>
                  {alerts.length}
                </span>
              </span>
            </button>
            <button
              onClick={() => setActiveTab('details')}
              className={`px-6 py-4 text-sm font-semibold transition-all ${
                activeTab === 'details'
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-800'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Details
              </span>
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'runs' && (
            <div className="overflow-x-auto">
              {runs.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 dark:text-gray-500 text-5xl mb-4">📊</div>
                  <p className="text-gray-500 dark:text-gray-400">No check runs yet</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Time</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Code</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Latency</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Error</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {runs.map((run, idx) => (
                      <tr key={run._id} className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-900/50'}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                          {new Date(run.ts).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge status={run.ok} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {run.statusCode ? (
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              run.statusCode >= 200 && run.statusCode < 300
                                ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                                : run.statusCode >= 300 && run.statusCode < 400
                                ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300'
                                : 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'
                            }`}>
                              {run.statusCode}
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {run.latencyMs ? (
                            <span className={`font-semibold ${
                              run.latencyMs < 500 ? 'text-green-600 dark:text-green-400' :
                              run.latencyMs < 1000 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'
                            }`}>
                              {run.latencyMs}ms
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-red-600 dark:text-red-400 max-w-md truncate">
                          {run.error || <span className="text-gray-400 dark:text-gray-500">-</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'alerts' && (
            <div className="space-y-4">
              {alerts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-green-500 dark:text-green-400 text-5xl mb-4">✅</div>
                  <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">No alerts for this monitor</p>
                  <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">All checks are passing!</p>
                </div>
              ) : (
                alerts.map((alert) => (
                  <div
                    key={alert._id}
                    className={`border-2 rounded-xl p-6 transition-all ${
                      alert.state === 'open' 
                        ? 'border-red-300 dark:border-red-700 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/30 shadow-md' 
                        : 'border-green-200 dark:border-green-700 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className={`rounded-full p-3 ${
                          alert.state === 'open' ? 'bg-red-200 dark:bg-red-800' : 'bg-green-200 dark:bg-green-800'
                        }`}>
                          {alert.state === 'open' ? (
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          ) : (
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className={`text-lg font-bold ${
                              alert.state === 'open' ? 'text-red-900 dark:text-red-300' : 'text-green-900 dark:text-green-300'
                            }`}>
                              {alert.state === 'open' ? 'Open Alert' : 'Closed Alert'}
                            </h3>
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              alert.state === 'open' 
                                ? 'bg-red-200 text-red-800' 
                                : 'bg-green-200 text-green-800'
                            }`}>
                              {alert.state === 'open' ? 'ACTIVE' : 'RESOLVED'}
                            </span>
                          </div>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center text-gray-700 dark:text-gray-300">
                              <span className="font-medium mr-2">Opened:</span>
                              {new Date(alert.openedAt).toLocaleString()}
                            </div>
                            {alert.closedAt && (
                              <div className="flex items-center text-gray-700 dark:text-gray-300">
                                <span className="font-medium mr-2">Closed:</span>
                                {new Date(alert.closedAt).toLocaleString()}
                              </div>
                            )}
                            <div className="flex items-center text-gray-700 dark:text-gray-300">
                              <span className="font-medium mr-2">Fail Count:</span>
                              <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded font-semibold">{alert.failCount}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'details' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Configuration
                </h3>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <dt className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Type</dt>
                    <dd className="text-sm font-bold text-gray-900 dark:text-white capitalize">{monitor.type.replace('_', ' ')}</dd>
                  </div>
                  {monitor.type !== 'ssl_check' && monitor.type !== 'heartbeat' && (
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                      <dt className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Method</dt>
                      <dd className="text-sm font-bold text-gray-900 dark:text-white">{monitor.method || 'N/A'}</dd>
                    </div>
                  )}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <dt className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Interval</dt>
                    <dd className="text-sm font-bold text-gray-900 dark:text-white">{monitor.intervalMinutes} minutes</dd>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <dt className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Timeout</dt>
                    <dd className="text-sm font-bold text-gray-900 dark:text-white">{monitor.timeoutMs}ms</dd>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <dt className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Fail Threshold</dt>
                    <dd className="text-sm font-bold text-gray-900 dark:text-white">{monitor.consecutiveFailThreshold} consecutive failures</dd>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <dt className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Environment</dt>
                    <dd className="text-sm">
                      <span className={`px-2 py-1 rounded font-semibold ${
                        monitor.env === 'prod' 
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' 
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                      }`}>
                        {monitor.env.toUpperCase()}
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>
              {monitor.notifyEmails && monitor.notifyEmails.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Notification Emails
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {monitor.notifyEmails.map((email, idx) => (
                      <span key={idx} className="inline-flex items-center px-3 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg text-sm font-medium border border-blue-200 dark:border-blue-700">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                        </svg>
                        {email}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ErrorModal
        isOpen={error !== null}
        onClose={() => setError(null)}
        title="Error"
        message={error || ''}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/70 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)}>
          <div 
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    Delete Monitor?
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Are you sure you want to delete "{monitor?.name}"? This action cannot be undone and will delete all check runs and alerts for this monitor.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 rounded-b-xl flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-6 py-2 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-6 py-2 bg-red-600 dark:bg-red-700 text-white rounded-lg font-semibold hover:bg-red-700 dark:hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitorDetail;
